import { FC, useContext, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuthContext } from "../../context/authContext";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import { SearchContextProvider, useSearchContext } from "../../context/searchContext";
import { useDebounce } from "../../hooks/useDebounce";
import useListFilters from "../../hooks/useListFilters";
import useI18n from "../../hooks/useTranslations";
import { SearchConfiguration } from "../../http/types/configurations";
import { useConfigurationsApi } from "../../http/useConfigurations";
import { PaginationQuery } from "../../types";
import { formatTimeStamp } from "../../utils";
import SvgCopy from "../icons/Copy";
import SvgEdit from "../icons/Edit";
import SvgVisibility from "../icons/Visibility";
import FlexContent from "../ui/FlexContent";
import Header from "../ui/Header";
import { Action, getRemoveAction } from "../ui/ListItemMenu";
import ModalDialog from "../ui/ModalDialog";
import SearchBar from "../ui/SearchBar";
import StatusBadge from "../ui/StatusBadge";
import Table from "../ui/Table";
import DuplicateConfigurationDialog from "./DuplicateConfigurationDialog";

const SharedConfigurations: FC = () => {
  return (
    <SearchContextProvider>
      <SharedConfigurationsContent />
    </SearchContextProvider>
  );
};

const SharedConfigurationsContent: FC = () => {
  const i18n = useI18n();
  const auth = useAuthContext();
  const navigate = useNavigate();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters();
  const aborter = useRef(new AbortController());
  const configurationsApi = useConfigurationsApi();
  const { actions, setActions } = useSearchContext();

  const debouncedValue = useDebounce(filters.state.q);
  const searchPlaceholder = i18n.translation.configurations.search;

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { data, error } = configurationsApi.useSharedConfigurations(query, aborter.current.signal);

  const [configurationToDuplicate, setConfigurationToDuplicate] = useState<SearchConfiguration>();
  const [configurationToRemove, setConfigurationToRemove] = useState<SearchConfiguration>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setActions(
      <SearchBar
        placeholder={searchPlaceholder}
        value={filters.state.q ?? ""}
        onChange={(term) => {
          aborter.current.abort();
          aborter.current = new AbortController();
          filters.setSearchTerm(term);
        }}
      />,
    );

    return () => {
      setActions(null);
    };
  }, [filters.state.q, searchPlaceholder, setActions]);

  const rowActions: (item: SearchConfiguration) => Action[] = (item) => [
    {
      title: item.canEdit ? i18n.translation.common.edit : i18n.translation.common.open,
      onClick: () => navigate(`/configurations/${item.id}`),
      icon: item.canEdit ? SvgEdit : SvgVisibility,
    },
    {
      title: i18n.translation.common.duplicate,
      onClick: () => setConfigurationToDuplicate(item),
      icon: SvgCopy,
    },
    getRemoveAction(i18n, () => setConfigurationToRemove(item), true),
  ];

  return (
    <>
      <Header
        title={i18n.translation.configurations.plural}
        path={{ display: i18n.translation.common.shared }}
        error={error}
        buttons={actions}
      />

      <FlexContent padding>
        <Table<SearchConfiguration>
          filters={filters}
          isTall
          head={
            <tr>
              <th align="left">{i18n.translation.common.name}</th>
              <th align="left">{i18n.translation.projects.singular}</th>
              <th align="left">{i18n.translation.systems.singular}</th>
              <th align="left">{i18n.translation.common.author}</th>
              <th align="left">{i18n.translation.common.lastModified}</th>
              <th align="center">{i18n.translation.common.canEdit}</th>
              <th></th>
            </tr>
          }
          items={data?.items}
          renderRow={(item) => (
            <>
              <td>
                <Link to={`/configurations/${item.id}`} onClick={(e) => e.stopPropagation()}>
                  {item.name}
                </Link>
              </td>
              <td>
                {item.project && (
                  <Link to={`/projects/${item.project.id}`} onClick={(e) => e.stopPropagation()}>
                    {item.project.name}
                  </Link>
                )}
              </td>
              <td>
                <Link to={`/systems/${item.system.id}`} onClick={(e) => e.stopPropagation()}>
                  {item.system.name}
                </Link>
              </td>
              <td>
                {item.author?.firstName} {item.author?.lastName}
              </td>
              <td>{formatTimeStamp(item.modifiedAt, auth.identity?.language)}</td>
              <td align="center">
                <StatusBadge color={item.canEdit ? "success" : "error"}>{item.canEdit ? "✔" : "✘"}</StatusBadge>
              </td>
            </>
          )}
          rowActions={rowActions}
          onRowClick={(item) => navigate(`/configurations/${item.id}`)}
          total={data?.total}
          error={!!error}
        />
        {!!configurationToDuplicate && (
          <DuplicateConfigurationDialog
            configuration={configurationToDuplicate}
            onClose={() => setConfigurationToDuplicate(undefined)}
            onSave={() => setConfigurationToDuplicate(undefined)}
          />
        )}
        {!!configurationToRemove && (
          <ModalDialog
            onClose={() => setConfigurationToRemove(undefined)}
            title={i18n.translation.configurations.removeShared}
            isSubmitting={isSubmitting}
            actions={[
              {
                title: i18n.translation.common.cancel,
                type: "secondary",
                onClick: () => setConfigurationToRemove(undefined),
              },
              {
                title: i18n.translation.common.remove,
                onClick: () => {
                  setIsSubmitting(true);
                  configurationsApi
                    .removeSharedConfiguration(configurationToRemove.id)
                    .then(() => setConfigurationToRemove(undefined))
                    .catch((err) => dispatch(showErrorNotification(i18n, err)))
                    .finally(() => setIsSubmitting(false));
                },
              },
            ]}
          >
            {i18n.translation.configurations.questions.removeShared}
          </ModalDialog>
        )}
      </FlexContent>
    </>
  );
};

export default SharedConfigurations;
